import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { PaginationSearchParams, SearchQuery, SortOrderQuery } from '@/api/types';
import { PaginatedHospitalListQueryParams } from '@/api/apis/facilities';
import { hospitalsQueryKeys } from '@/queries/hospital/hospital.keys';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '@/queries/utils';

const allHospitalsPaginationParams: PaginationSearchParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

export const hospitalQueries = {
  all: () =>
    queryOptions({
      queryKey: hospitalsQueryKeys.getList(allHospitalsPaginationParams),
      queryFn: () => api.hospital.getHospitalList(allHospitalsPaginationParams),
    }),
  list: (listQuery: PaginatedHospitalListQueryParams) =>
    queryOptions({
      queryKey: hospitalsQueryKeys.getList(listQuery),
      queryFn: () => api.hospital.getHospitalList(listQuery),
      placeholderData: keepPreviousData,
    }),
  infiniteList: (searchPagination: PaginationSearchParams & SearchQuery & SortOrderQuery) =>
    infiniteQueryOptions({
      queryKey: hospitalsQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) => api.hospital.getHospitalList({ ...searchPagination, pageIndex }),
      placeholderData: keepPreviousData,
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
  item: (hospitalId: string) =>
    queryOptions({
      queryKey: hospitalsQueryKeys.getItem(hospitalId),
      queryFn: () => api.hospital.getHospitalById(hospitalId),
    }),
};

const useHospitalListQuery = (listQuery: PaginatedHospitalListQueryParams) => {
  return useQuery(hospitalQueries.list(listQuery));
};

const useInfiniteHospitalListQuery = (filters: SearchQuery & SortOrderQuery) => {
  return useInfiniteQuery(hospitalQueries.infiniteList({ itemsPerPage: 10, pageIndex: 0, ...filters }));
};

export { useHospitalListQuery, useInfiniteHospitalListQuery };
